import { useQuery } from '@tanstack/react-query'
import { ArrowRight, BookOpen, Layers, MoreHorizontal, Pencil, Plus, Trash2, UsersRound } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { CLASS_CYCLES } from '@/features/school/vocabulary'
import { Link } from 'react-router'
import { EmptyState, ErrorState } from '@/components/feedback/States'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu'
import { PageHeader } from '@/components/ui/PageHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { classesQuery, useDeleteClass } from '@/features/academic/api'
import { ClassDialog } from '@/features/academic/components/ClassDialog'
import { DeleteDialog } from '@/features/academic/components/DeleteDialog'
import { OccupancyBar } from '@/features/academic/components/OccupancyBar'
import type { SchoolClass } from '@/features/academic/types'
import { Can } from '@/features/auth/guards'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { tAny } from '@/i18n'
import { formatNumber } from '@/utils/format'

function ageRange(c: SchoolClass): string {
  if (c.minAge !== null && c.maxAge !== null)
    return tAny('Ages {{min}}–{{max}}', { min: c.minAge, max: c.maxAge })
  if (c.minAge !== null) return tAny('Ages {{min}}+', { min: c.minAge })
  if (c.maxAge !== null) return tAny('Up to {{max}}', { max: c.maxAge })
  return tAny('Any age')
}

/** Keeps classes of one level together (e.g. Première L next to Première S), in API order otherwise. */
function byLevel(classes: SchoolClass[]): SchoolClass[] {
  return [...classes].sort((a, b) => a.level - b.level)
}

export function ClassesPage() {
  const { t } = useTranslation('academic')
  useDocumentTitle('Classes')
  const classes = useQuery(classesQuery())
  const remove = useDeleteClass()
  const [editing, setEditing] = useState<SchoolClass | 'new' | null>(null)
  const [deleting, setDeleting] = useState<SchoolClass | null>(null)
  const nextLevel = Math.max(0, ...(classes.data ?? []).map((c) => c.level)) + 1

  const addButton = (
    <Can permission="academic.manage">
      <Button leftIcon={<Plus />} onClick={() => setEditing('new')}>
        {t('New class')}
      </Button>
    </Can>
  )

  return (
    <>
      <PageHeader
        title={t('Classes')}
        description={t('Grades, their sections and how full each one is.')}
        breadcrumbs={[{ label: t('Academic') }, { label: t('Classes') }]}
        homeHref="/admin"
        actions={addButton}
      />

      {classes.isPending ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }, (_, i) => (
            <Card key={i} className="space-y-3 p-5">
              <Skeleton className="h-5 w-1/3" />
              <Skeleton className="h-3.5 w-1/2" />
              <Skeleton className="h-8 w-full" />
            </Card>
          ))}
        </div>
      ) : classes.error ? (
        <Card>
          <ErrorState onRetry={() => void classes.refetch()} />
        </Card>
      ) : classes.data.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Layers />}
            title={t('No classes yet')}
            description={t('Create your first class, then add sections to it.')}
            action={addButton}
          />
        </Card>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {byLevel(classes.data).map((c) => (
            <li key={c.id}>
              <ClassCard schoolClass={c} onEdit={() => setEditing(c)} onDelete={() => setDeleting(c)} />
            </li>
          ))}
        </ul>
      )}

      <ClassDialog
        open={editing !== null}
        onOpenChange={(open) => (open ? undefined : setEditing(null))}
        schoolClass={editing === 'new' ? null : editing}
        nextLevel={nextLevel}
      />
      <DeleteDialog
        item={deleting}
        onClose={() => setDeleting(null)}
        title={(c) => t('Delete {{name}}?', { name: c.name })}
        description={(c) =>
          c.studentCount > 0
            ? t(
                '{{count}} students are enrolled in this class. Move them first — a class in use cannot be deleted.',
                { count: c.studentCount, formatted: formatNumber(c.studentCount) },
              )
            : t('Its sections and curriculum will be removed. This cannot be undone.')
        }
        onDelete={(c) => remove.mutateAsync(c.id)}
        successMessage={(c) => t('{{name}} deleted', { name: c.name })}
      />
    </>
  )
}

function ClassCard({
  schoolClass: c,
  onEdit,
  onDelete,
}: {
  schoolClass: SchoolClass
  onEdit: () => void
  onDelete: () => void
}) {
  const { t } = useTranslation('academic')
  const href = `/admin/academic/classes/${c.id}`
  const capacity = c.sections.reduce((sum, s) => sum + (s.capacity ?? 0), 0)

  return (
    <Card className="flex h-full flex-col transition-shadow hover:shadow-raised">
      <div className="flex items-start gap-3 p-5 pb-4">
        <span
          className="grid size-11 shrink-0 place-items-center rounded-control bg-brand-50 text-base font-semibold text-brand-700 tabular"
          aria-hidden="true"
        >
          {c.level}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <h2 className="truncate text-base font-semibold">
              <Link to={href} className="hover:text-brand-700">
                {c.name}
              </Link>
            </h2>
            {c.series ? (
              <Badge size="sm" className="shrink-0" title={t('Series')}>
                {c.series}
              </Badge>
            ) : null}
          </div>
          <p className="mt-0.5 text-xs text-muted">
            {t('Level {{level}}', { level: c.level })} · {ageRange(c)}
            {c.cycle ? ` · ${tAny(CLASS_CYCLES.find((k) => k.value === c.cycle)?.label ?? c.cycle)}` : ''}
          </p>
        </div>
        <Can permission="academic.manage">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <IconButton
                label={t('Actions for {{name}}', { name: c.name })}
                icon={<MoreHorizontal />}
                size="icon-sm"
                className="-mt-1 -mr-2"
              />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem icon={<Pencil />} onSelect={onEdit}>
                {t('Edit class')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem icon={<Trash2 />} tone="danger" onSelect={onDelete}>
                {t('Delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </Can>
      </div>

      <dl className="grid grid-cols-3 gap-2 border-y border-line bg-surface-muted/60 px-5 py-3 text-center">
        <Stat icon={<UsersRound />} label={t('Students')} value={formatNumber(c.studentCount)} />
        <Stat icon={<Layers />} label={t('Sections')} value={formatNumber(c.sections.length)} />
        <Stat icon={<BookOpen />} label={t('Subjects')} value={formatNumber(c.subjects.length)} />
      </dl>

      <div className="flex-1 px-5 py-4">
        {c.sections.length === 0 ? (
          <p className="text-sm text-muted">{t('No sections yet.')}</p>
        ) : (
          <ul className="flex flex-wrap gap-2" aria-label={t('{{name}} sections', { name: c.name })}>
            {c.sections.map((s) => (
              <li
                key={s.id}
                className="min-w-[6.5rem] rounded-control border border-line bg-surface px-2.5 py-1.5"
              >
                <p className="text-xs font-medium text-ink tabular">
                  {s.name || t('Whole class')}{' '}
                  <span className="text-muted">
                    · {s.studentCount}
                    {s.capacity ? `/${s.capacity}` : ''}
                  </span>
                </p>
                <OccupancyBar
                  count={s.studentCount}
                  capacity={s.capacity}
                  label={
                    s.name
                      ? t('Section {{name}} occupancy', { name: s.name })
                      : t('{{name}} occupancy', { name: c.name })
                  }
                  className="mt-1 h-1"
                />
              </li>
            ))}
          </ul>
        )}
        {capacity > 0 ? (
          <p className="mt-3 text-xs text-muted">
            {t('{{filled}} of {{capacity}} seats filled', {
              filled: formatNumber(c.studentCount),
              capacity: formatNumber(capacity),
            })}
            {c.studentCount >= capacity ? (
              <Badge tone="danger" size="sm" className="ml-2">
                {t('Full')}
              </Badge>
            ) : null}
          </p>
        ) : null}
      </div>

      <div className="border-t border-line px-5 py-3">
        <Link
          to={href}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:text-brand-700"
        >
          {t('Manage class')} <ArrowRight className="size-4" aria-hidden="true" />
        </Link>
      </div>
    </Card>
  )
}

function Stat({ icon, label, value }: { icon: ReactNode; label: string; value: string }) {
  return (
    <div>
      <dt className="flex items-center justify-center gap-1 text-2xs font-medium tracking-wide text-muted uppercase [&_svg]:size-3">
        {icon}
        {label}
      </dt>
      <dd className="mt-0.5 text-base font-semibold text-ink tabular">{value}</dd>
    </div>
  )
}
